/**
 * Spec 059 — Garagat-funded promo codes. Shapes mirror design.md §3 "Admin — orders":
 * `/api/admin/promotions/**` (screen `promotions`) and `/api/admin/promotions/settings`
 * (screen `finance-config` since #1113). Money is AED, VAT-inclusive, as numbers.
 */

export type PromoAudience = 'PUBLIC' | 'INVITE_ONLY';
export type PromoDiscountType = 'PERCENT' | 'FIXED';
export type PromoDerivedStatus = 'SCHEDULED' | 'ACTIVE' | 'DISABLED' | 'EXPIRED' | 'FULL';

export const PROMO_STATUSES: PromoDerivedStatus[] = ['ACTIVE', 'SCHEDULED', 'DISABLED', 'EXPIRED', 'FULL'];

/** `GET /api/admin/promotions` row. */
export interface PromoCodeSummary {
  id: string;
  code: string;
  name: string;
  audience: PromoAudience;
  discountType: PromoDiscountType;
  discountValue: number;
  /** PERCENT only; null = no ceiling per use beyond the global cap. */
  maxDiscountPerUse: number | null;
  derivedStatus: PromoDerivedStatus;
  startsAt: string;
  /** null = no expiry. */
  expiresAt: string | null;
  holdersCount: number;
  maxHolders: number;
  usesPerHolder: number;
  /** Garagat spend on this code (AED). */
  spend: number;
}

export interface PromoCodeStats {
  holders: number;
  pendingHolders: number;
  usesReserved: number;
  usesRedeemed: number;
  spendRedeemed: number;
  spendReserved: number;
  /** Full-price service value of redeemed orders. */
  fullPriceValue: number;
}

/** `GET /api/admin/promotions/{id}` (also the body of create / update / disable / enable). */
export interface PromoCodeDetail extends PromoCodeSummary {
  version: number;
  /** True from the first holder: type, value, max per use and start can no longer change (BR-8). */
  termsLocked: boolean;
  /** `https://garagat.ai/promo/{CODE}` */
  shareLink: string;
  createdBy: string | null;
  createdAt: string;
  stats: PromoCodeStats;
}

/** `POST /api/admin/promotions`. */
export interface PromoCodeCreateRequest {
  code: string;
  name: string;
  audience: PromoAudience;
  discountType: PromoDiscountType;
  discountValue: number;
  maxDiscountPerUse: number | null;
  usesPerHolder: number;
  maxHolders: number;
  startsAt: string | null;
  expiresAt: string | null;
}

/**
 * `PUT /api/admin/promotions/{id}`. The locked fields are sent only while `termsLocked` is false;
 * `expiresAt: null` removes the expiry.
 */
export interface PromoCodeUpdateRequest {
  version: number;
  name: string;
  audience: PromoAudience;
  expiresAt: string | null;
  maxHolders: number;
  usesPerHolder: number;
  discountType?: PromoDiscountType;
  discountValue?: number;
  maxDiscountPerUse?: number | null;
  startsAt?: string | null;
}

export interface PromoListQuery {
  status?: PromoDerivedStatus;
  search?: string;
  audience?: PromoAudience;
  cursor?: string;
  size?: number;
}

export type PromoHolderStatus = 'PENDING' | 'ACTIVE' | 'REVOKED';
export type PromoHolderSource = 'ADMIN_SHARE' | 'LINK_CLAIM' | 'TYPED_CLAIM' | 'WALLET_CLAIM';
export type PromoChannel = 'WHATSAPP' | 'EMAIL';
export type PromoMessageStatus = 'PENDING' | 'SENT' | 'DELIVERED' | 'READ' | 'FAILED' | 'OPTED_OUT';

export interface PromoHolderMessage {
  id: string;
  channel: PromoChannel;
  status: PromoMessageStatus;
  error: string | null;
  updatedAt: string | null;
}

/** `GET /api/admin/promotions/{id}/holders` row. */
export interface PromoHolderRow {
  id: string;
  customer: { subjectId: string; name: string | null; phone: string | null; email: string | null } | null;
  pendingPhone: string | null;
  pendingEmail: string | null;
  source: PromoHolderSource | string;
  sharedBy: { name: string | null } | null;
  sharedAt: string | null;
  claimedAt: string | null;
  status: PromoHolderStatus;
  usesConsumed: number;
  usesPerHolder: number;
  messages: PromoHolderMessage[];
  lastOrder: { id: string; orderNumber: string | null } | null;
}

export type ShareRecipient =
  | { type: 'CUSTOMER'; subjectId: string; channels: PromoChannel[] }
  | { type: 'EMAIL'; email: string }
  | { type: 'PHONE'; phone: string };

export type ShareOutcome = 'SHARED' | 'ALREADY_HOLDS' | 'FULL' | 'INVALID_TARGET' | 'CHANNEL_UNAVAILABLE';

export interface ShareResult {
  /** The recipient as the server echoes it; results come back in request order. */
  recipient: ShareRecipient | string | null;
  outcome: ShareOutcome;
  holderId?: string | null;
}

/** `POST /api/admin/promotions/{id}/shares` → 200. */
export interface ShareResponse {
  results: ShareResult[];
}

/** `GET /api/admin/promotions/customers` row (customer picker). */
export interface PromoCustomer {
  subjectId: string;
  name: string | null;
  phone: string | null;
  email: string | null;
  hasEmail: boolean;
}

/** `GET /api/admin/promotions/settings` (screen `finance-config` since #1113). */
export interface PromoSettings {
  maxDiscountPercent: number;
  updatedBy: string | null;
  /** Spec 069 — the admin's display name, resolved server-side. */
  updatedByName?: string | null;
  updatedAt: string | null;
  codesAboveCap: { id: string; code: string; discountValue: number }[];
}

export interface CursorPage<T> {
  content: T[];
  nextCursor?: string | null;
  hasMore: boolean;
  size?: number;
}
